import { Switch, Match, Show, For } from 'solid-js';
import { FormProps } from './mautic-form-types';
import { ErrorCatcher } from '../../tools/error-catcher';
import { Input } from './input-fields/input';
import { FieldContainer } from './field-container';
import { Textarea } from './input-fields/textarea';
import { Select } from './input-fields/select';
import { Checkbox } from './input-fields/checkbox';
import { Button } from '../../ui-components/button/button';
import { ButtonColorName } from '../../style/color-classes';
import { RadioGroup } from './input-fields/radio-group';
import { Heading } from '../../ui-components/heading/heading';
import { getClasses, getErrorClasses } from './classes';
import clsx from 'clsx';
import { useMauticForm } from './use-mautic-form';
import { useSectionTheme } from './use-section-theme';
import { ErrorIcon } from '../icons-library/error';
import { Turnstile } from './turnstile';

const FieldType = {
    TEXTAREA: 'textarea',
    RADIO: 'radiogrp',
    CHECKBOX: 'checkboxgrp',
    SELECT: 'select',
    BUTTON: 'button',
    SUBMIT: 'submit',
    HIDDEN: 'hidden',
} as const;

export const MauticForm = (props: FormProps) => {
    if (!props.form?.fields) {
        return null;
    }

    const {
        errors,
        loading,
        success,
        handleSubmit,
        getFieldProps,
        turnstileSiteKey,
        setTurnstileToken,
        registerTurnstileReset,
        setTurnstileUnavailable,
    } = useMauticForm(props);
    const { setRef, errorClass } = useSectionTheme();

    const variant = props.variant ?? 'dark';
    // 'outline-dark' is the only variant rendered on a light surface.
    const turnstileTheme = variant === 'outline-dark' ? 'light' : 'dark';
    const errorClasses = getErrorClasses(variant);
    const fields = props.form.fields;

    const inputLabelClasses = getClasses('label', variant);
    const inputClasses = getClasses('input', variant);
    const radioInputClasses = getClasses('radio', variant);
    const checkboxInputClasses = getClasses('checkbox', variant);
    const selectClasses = getClasses('select', variant);

    const submitField = fields.find((f) => f.type === FieldType.SUBMIT || (f.type === FieldType.BUTTON && f.alias === FieldType.SUBMIT));

    return (
        <ErrorCatcher componentName="MauticForm">
            <form
                ref={setRef}
                class="tablet:grid tablet:grid-cols-2 flex w-full flex-col gap-x-8 gap-y-10 p-4"
                onSubmit={handleSubmit}
                data-form-id={props.form.id}
            >
                <For each={fields.filter((f) => f.alias !== FieldType.SUBMIT)}>
                    {(field) => {
                        const baseProps = getFieldProps(field);
                        const errorId = `${field.alias}-error`;

                        if (field.type === FieldType.HIDDEN) {
                            return (
                                <input
                                    type="hidden"
                                    id={String(field.id)}
                                    name={field.alias}
                                    value={typeof baseProps.value === 'boolean' ? String(baseProps.value) : (baseProps.value as string) ?? ''}
                                />
                            );
                        }

                        return (
                            <FieldContainer attributes={field?.containerAttributes as string}>
                                <Switch
                                    fallback={
                                        <Input
                                            fieldData={{
                                                ...baseProps,
                                                inputClasses,
                                                inputLabelClasses,
                                                errorInputClasses: errorClasses.input,
                                                errorLabelClasses: errorClasses.label,
                                                errorId,
                                            }}
                                        />
                                    }
                                >
                                    <Match when={field.type === FieldType.TEXTAREA}>
                                        <Textarea
                                            fieldData={{
                                                ...baseProps,
                                                inputClasses,
                                                inputLabelClasses,
                                                errorInputClasses: errorClasses.input,
                                                errorLabelClasses: errorClasses.label,
                                                errorId,
                                            }}
                                        />
                                    </Match>
                                    <Match when={field.type === FieldType.RADIO}>
                                        <RadioGroup fieldData={{ ...baseProps, radioInputClasses, errorInputClasses: errorClasses.input, errorId }} />
                                    </Match>
                                    <Match when={field.type === FieldType.CHECKBOX}>
                                        <Checkbox fieldData={{ ...baseProps, checkboxInputClasses, errorInputClasses: errorClasses.input, errorId }} />
                                    </Match>
                                    <Match when={field.type === FieldType.SELECT}>
                                        <Select
                                            fieldData={{
                                                ...baseProps,
                                                inputClasses: selectClasses,
                                                inputLabelClasses,
                                                errorInputClasses: errorClasses.input,
                                                errorLabelClasses: errorClasses.label,
                                                errorId,
                                            }}
                                        />
                                    </Match>
                                    <Match when={field.type === FieldType.BUTTON && field.alias !== FieldType.SUBMIT}>
                                        <label class="sr-only" for={field.id.toLocaleString()}>
                                            {field.label}
                                        </label>
                                        <Button
                                            id={field.id.toLocaleString()}
                                            color={variant === 'dark' || variant === 'outline-light' ? ButtonColorName.Secondary : ButtonColorName.Primary}
                                            label={field.label}
                                            variant="solid"
                                            buttonSize="large"
                                            arrow={true}
                                            fullWidth={true}
                                        />
                                    </Match>
                                </Switch>
                                <Show when={errors[field.alias]}>
                                    <div id={errorId} class={clsx('absolute mt-2 flex items-start gap-2 pl-1', errorClass())} role="alert">
                                        <span class="mt-[1px] shrink-0">
                                            <ErrorIcon ariaHidden={true} focusable={false} />
                                        </span>
                                        <span class="text-paragraphXS line-clamp-1">{errors[field.alias]}</span>
                                    </div>
                                </Show>
                            </FieldContainer>
                        );
                    }}
                </For>

                <Show when={turnstileSiteKey}>
                    <div class="col-span-2">
                        <Turnstile
                            siteKey={turnstileSiteKey}
                            theme={turnstileTheme}
                            onVerify={setTurnstileToken}
                            registerReset={registerTurnstileReset}
                            onUnavailable={() => setTurnstileUnavailable(true)}
                        />
                    </div>
                </Show>

                <Show when={errors['_form']}>
                    <div class={clsx('col-span-2 flex items-start gap-2 pl-1', errorClass())} role="alert">
                        <span class="mt-[1px] shrink-0">
                            <ErrorIcon ariaHidden={true} focusable={false} />
                        </span>
                        <span class="text-paragraphXS">{errors['_form']}</span>
                    </div>
                </Show>

                <Show when={submitField}>
                    <div class="pt-s col-span-2">
                        <label class="sr-only" for={submitField?.id.toLocaleString()}>
                            {submitField?.label}
                        </label>
                        <Button
                            id={submitField?.id.toLocaleString()}
                            color={variant === 'dark' ? ButtonColorName.White : ButtonColorName.Secondary}
                            disabled={loading() || !!success()}
                            label={submitField?.label || 'Skicka'}
                            variant="solid"
                            buttonSize="large"
                            arrow={true}
                            fullWidth={true}
                            type="submit"
                        />
                    </div>
                </Show>

                <Show when={success()}>
                    <div class="col-span-2 flex justify-center">
                        <Heading
                            tag="h4"
                            align="center"
                            classes={clsx(
                                'col-span-2',
                                { 'text-secondary-200': variant === 'dark' },
                                { 'text-neutral-900': variant === 'outline-dark' },
                                { 'text-white': variant === 'light' || variant === 'outline-light' },
                            )}
                        >
                            {success()}
                        </Heading>
                    </div>
                </Show>
            </form>
        </ErrorCatcher>
    );
};

MauticForm.parseProps = (atts: any) => {
    const { form, mauticFormId, variant } = atts;

    const formProps: FormProps = {
        variant,
        form,
        mauticFormId,
    };

    return formProps;
};
